import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { GetApp, InfoOutlined } from '@material-ui/icons';
import { ROLES, normalizeDepartment, resolveDepartmentTabs } from '../shared/constants';
import { downloadTimesheetWorkbook } from '../shared/exportWorkbook';
import { useTimesheetTableStyles } from '../shared/tableStyles';
import { useTimesheetPageStyles } from '../shared/pageStyles';
import EngineerAutocomplete from '../shared/EngineerAutocomplete';
import { useInfiniteRows } from '../shared/useInfiniteRows';
import {
  DEPARTMENT_PREVIEW_ROWS,
  EXPORT_DEFAULT_RANGE_DAYS,
  fetchTimesheetExport,
  localDateDaysAgo,
} from '../api';
import { formatWorkDateDisplay } from '../shared/format';

const PRIMARY = '#00236f';
const SECONDARY = '#505f76';
const OUTLINE = '#c5c5d3';
const SURFACE_VARIANT = '#e0e3e5';
const SECONDARY_CONTAINER = '#d0e1fb';
const ON_SECONDARY_CONTAINER = '#54647a';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: 40,
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflowX: 'hidden',
  },
  header: {
    marginBottom: 0,
  },
  subtitle: {
    color: SECONDARY,
    fontSize: 18,
    lineHeight: '28px',
  },
  note: {
    background: SECONDARY_CONTAINER,
    border: `1px solid #d3e4fe`,
    color: ON_SECONDARY_CONTAINER,
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
  },
  noteIcon: {
    marginTop: 2,
    fontSize: 22,
    color: PRIMARY,
    flexShrink: 0,
  },
  actionBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    alignItems: 'flex-end',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    paddingBottom: 16,
    borderBottom: `1px solid ${SURFACE_VARIANT}`,
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    alignItems: 'flex-end',
    flex: '1 1 280px',
    minWidth: 0,
    maxWidth: '100%',
  },
  clearBtn: {
    height: 40,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontWeight: 700,
    fontSize: 12,
    color: SECONDARY,
    // Force visible border over MUI outlined styles.
    border: `1px solid ${OUTLINE} !important`,
    borderColor: `${OUTLINE} !important`,
    '&:hover': {
      border: `1px solid ${OUTLINE} !important`,
      borderColor: `${OUTLINE} !important`,
    },
    '&.Mui-disabled': {
      border: `1px solid ${OUTLINE} !important`,
      borderColor: `${OUTLINE} !important`,
      opacity: 0.85,
    },
  },
  downloadBtn: {
    display: 'inline-flex !important',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 40,
    minHeight: 40,
    maxHeight: 40,
    padding: '0 24px !important',
    margin: 0,
    boxSizing: 'border-box',
    background: SURFACE_VARIANT,
    color: '#444651',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontWeight: 700,
    fontSize: 12,
    lineHeight: '16px',
    flex: '0 0 auto',
    alignSelf: 'flex-end',
    marginLeft: 'auto',
    '&.Mui-disabled': {
      background: SURFACE_VARIANT,
      color: '#444651',
      opacity: 0.85,
    },
    '& .MuiButton-label': {
      display: 'inline-flex !important',
      flexDirection: 'row !important',
      alignItems: 'center !important',
      justifyContent: 'center',
      height: 'auto',
      width: 'auto',
      whiteSpace: 'nowrap',
      padding: '0 !important',
      margin: '0 !important',
      lineHeight: '16px',
    },
  },
  downloadInner: {
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    lineHeight: '16px',
  },
  downloadIcon: {
    fontSize: '16px !important',
    width: '16px !important',
    height: '16px !important',
    display: 'block',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },
  sectionHint: {
    color: SECONDARY,
    fontSize: 14,
    marginTop: -8,
  },
  tabs: {
    display: 'flex',
    borderBottom: `1px solid ${SURFACE_VARIANT}`,
    marginBottom: 16,
  },
  tab: {
    minWidth: 0,
    padding: '12px 24px',
    borderRadius: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontSize: 12,
    fontWeight: 500,
    color: SECONDARY,
    borderBottom: '2px solid transparent',
    '&:hover': {
      background: '#f2f4f6',
      color: '#191c1e',
    },
  },
  tabActive: {
    color: `${PRIMARY} !important`,
    fontWeight: 700,
    borderBottom: `2px solid ${PRIMARY}`,
    background: 'transparent',
    '&:hover': {
      background: '#f2f4f6',
      color: `${PRIMARY} !important`,
    },
  },
});

const TimesheetExport = ({ currentUser, users, appname, fmId }) => {
  const classes = useStyles();
  const pageClasses = useTimesheetPageStyles();
  const tableClasses = useTimesheetTableStyles();
  const engineers = users.filter(u => u.role === ROLES.ENGINEER);
  const scopedEngineers = useMemo(
    () =>
      engineers.filter(e =>
        currentUser.role === ROLES.SYSTEM_ADMIN
          ? true
          : e.assignedFmId === currentUser.id,
      ),
    [engineers, currentUser.role, currentUser.id],
  );
  const [engineerId, setEngineerId] = useState('all');
  const [fromDate, setFromDate] = useState(() =>
    localDateDaysAgo(EXPORT_DEFAULT_RANGE_DAYS),
  );
  const [toDate, setToDate] = useState(() => localDateDaysAgo(0));
  const [tab, setTab] = useState(0);
  const [masterRows, setMasterRows] = useState([]);
  const [departmentTabs, setDepartmentTabs] = useState({});
  const [departmentOrder, setDepartmentOrder] = useState([]);
  const [previewLimit, setPreviewLimit] = useState(DEPARTMENT_PREVIEW_ROWS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const scopeParams = useMemo(
    () => ({
      appname,
      fmId: fmId || undefined,
      engineerId: engineerId !== 'all' ? engineerId : undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    }),
    [appname, fmId, engineerId, fromDate, toDate],
  );

  useEffect(() => {
    let cancelled = false;
    if (!appname) return undefined;

    setLoading(true);
    setLoadError(null);
    fetchTimesheetExport({
      ...scopeParams,
      departmentLimit: DEPARTMENT_PREVIEW_ROWS,
    })
      .then(data => {
        if (cancelled) return;
        setMasterRows(data.master || []);
        setDepartmentTabs(data.departments || {});
        setDepartmentOrder(
          data.departmentOrder ||
            resolveDepartmentTabs(data.departments || {}),
        );
        setPreviewLimit(data.departmentLimit || DEPARTMENT_PREVIEW_ROWS);
      })
      .catch(err => {
        if (cancelled) return;
        console.error(err);
        setMasterRows([]);
        setDepartmentTabs({});
        setDepartmentOrder([]);
        setLoadError(err.message || 'Failed to load export data');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [appname, scopeParams]);

  const masterInfinite = useInfiniteRows(masterRows, {
    pageSize: 25,
    resetKey: `master|${engineerId}|${fromDate}|${toDate}|${masterRows.length}`,
  });

  const engineerName = id => {
    const eng = engineers.find(u => u.id === id);
    return eng ? eng.name : id;
  };

  const departmentNames = useMemo(
    () =>
      departmentOrder.length
        ? departmentOrder
        : resolveDepartmentTabs(departmentTabs),
    [departmentOrder, departmentTabs],
  );

  useEffect(() => {
    if (tab >= departmentNames.length && departmentNames.length > 0) {
      setTab(0);
    }
  }, [departmentNames.length, tab]);

  const dept = departmentNames[tab] || departmentNames[0] || '';
  const deptRows = departmentTabs[dept] || [];
  const deptInfinite = useInfiniteRows(deptRows, {
    pageSize: 25,
    resetKey: `dept|${dept}|${engineerId}|${fromDate}|${toDate}|${deptRows.length}`,
  });

  const defaultFromDate = localDateDaysAgo(EXPORT_DEFAULT_RANGE_DAYS);
  const defaultToDate = localDateDaysAgo(0);

  const hasActiveFilters =
    engineerId !== 'all' ||
    fromDate !== defaultFromDate ||
    toDate !== defaultToDate;

  const hasExportData =
    masterRows.length > 0 ||
    Object.values(departmentTabs).some(rows => rows.length > 0);

  const downloadFull = async () => {
    if (!appname || downloading || !hasExportData) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      // Full history for scoped engineers — no departmentLimit on download.
      const data = await fetchTimesheetExport(scopeParams);
      if (
        !(data.master || []).length &&
        !Object.values(data.departments || {}).some(rows => rows.length > 0)
      ) {
        setDownloadError('No export data for the current filters.');
        return;
      }
      downloadTimesheetWorkbook(
        data.master || [],
        data.departments || {},
        localDateDaysAgo(0),
        data.departmentOrder ||
          resolveDepartmentTabs(data.departments || {}),
      );
    } catch (err) {
      console.error(err);
      setDownloadError(err.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box className={classes.page}>
      <Box className={classes.header}>
        <Typography className={pageClasses.pageTitle} style={{ marginBottom: 8 }}>
          Excel Export
        </Typography>
        <Typography className={classes.subtitle}>
          Approved entries only. Download Excel for the complete data set across
          the selected date range.
        </Typography>
      </Box>

      <Box className={classes.note}>
        <InfoOutlined className={classes.noteIcon} />
        <Typography variant="body2">
          The department tabs below are a preview only, showing the latest{' '}
          {previewLimit} approved entries per department. Download Excel to get
          every approved entry in the selected date range. Pending and refused
          entries are excluded throughout. Sales Commission, Expenses, Mileage
          and Other Deduction are left blank for CSS to complete.
        </Typography>
      </Box>

      <Box className={classes.actionBar}>
        <Box className={classes.filters}>
          <EngineerAutocomplete
            engineers={scopedEngineers}
            value={engineerId}
            onChange={setEngineerId}
            label="Filter engineer"
            allLabel="All assigned engineers"
            style={{ minWidth: 240, maxWidth: 360, flex: '1 1 240px' }}
          />
          <TextField
            label="From"
            type="date"
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            inputProps={{ max: toDate || undefined, lang: 'en-GB' }}
          />
          <TextField
            label="To"
            type="date"
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            inputProps={{ min: fromDate || undefined, lang: 'en-GB' }}
          />
          <Button
            variant="outlined"
            className={classes.clearBtn}
            onClick={() => {
              setEngineerId('all');
              setFromDate(localDateDaysAgo(EXPORT_DEFAULT_RANGE_DAYS));
              setToDate(localDateDaysAgo(0));
            }}
            disabled={!hasActiveFilters}
          >
            Clear filters
          </Button>
        </Box>
        <Button
          className={classes.downloadBtn}
          onClick={downloadFull}
          disabled={downloading || loading || !hasExportData}
        >
          <span className={classes.downloadInner}>
            <GetApp className={classes.downloadIcon} />
            <span>{downloading ? 'Preparing…' : 'Download Excel'}</span>
          </span>
        </Button>
      </Box>

      {loadError ? (
        <Typography color="error">{loadError}</Typography>
      ) : null}
      {downloadError ? (
        <Typography color="error">{downloadError}</Typography>
      ) : null}

      {loading ? (
        <Box p={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box className={classes.section}>
            <Typography className={pageClasses.pageTitle}>
              MASTER tab (aggregates)
            </Typography>
            <TableContainer
              component={Paper}
              className={tableClasses.tablePaper}
              onScroll={masterInfinite.onTableScroll}
            >
              <Table className={tableClasses.table} stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Engineer</TableCell>
                    <TableCell>STANDARD HRS</TableCell>
                    <TableCell>OVERTIME 1.5× (hrs)</TableCell>
                    <TableCell>DOUBLE TIME 2.0× (hrs)</TableCell>
                    <TableCell>Entries</TableCell>
                    <TableCell>MILEAGE(miles)</TableCell>
                    <TableCell>Sales Commission</TableCell>
                    <TableCell>MILEAGE DEDUCTION(£)</TableCell>
                    <TableCell>EXPENSES(£)</TableCell>
                    <TableCell>Other Deduction</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {masterRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <Typography color="textSecondary">
                          No matching rows.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    masterInfinite.visibleRows.map(row => (
                      <TableRow key={row.engineerId} hover>
                        <TableCell>
                          {row.engineerName || engineerName(row.engineerId)}
                        </TableCell>
                        <TableCell>{row.standardHrs}</TableCell>
                        <TableCell>{row.overtime15Hrs || 0}</TableCell>
                        <TableCell>{row.overtime20Hrs || 0}</TableCell>
                        <TableCell>{row.entries}</TableCell>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell />
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {masterRows.length > 0 ? (
                <Typography className={tableClasses.scrollHint}>
                  {masterInfinite.hasMore
                    ? `Showing ${masterInfinite.visibleCount} of ${masterInfinite.totalCount} — scroll for more`
                    : `Showing all ${masterInfinite.totalCount} rows`}
                </Typography>
              ) : null}
            </TableContainer>
          </Box>

          <Box className={classes.section}>
            <Typography className={pageClasses.pageTitle}>
              DEPARTMENT tabs
            </Typography>
            <Typography className={classes.sectionHint}>
              Preview: latest {previewLimit} entries per department. Download
              Excel for complete department history.
            </Typography>
            <Box className={classes.tabs}>
              {departmentNames.map((d, index) => (
                <Button
                  key={d}
                  disableRipple
                  className={`${classes.tab} ${
                    tab === index ? classes.tabActive : ''
                  }`}
                  onClick={() => setTab(index)}
                >
                  {d}
                </Button>
              ))}
            </Box>
            <TableContainer
              component={Paper}
              className={tableClasses.tablePaper}
              onScroll={deptInfinite.onTableScroll}
            >
              <Table className={tableClasses.table} stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Engineer</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>MULTIPLIER</TableCell>
                    <TableCell>TOTAL HRS</TableCell>
                    <TableCell>MILEAGE(miles)</TableCell>
                    <TableCell>MILEAGE DEDUCTION(£)</TableCell>
                    <TableCell>EXPENSES(£)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deptRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography color="textSecondary">
                          No entries for {dept}.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    deptInfinite.visibleRows.map((e, idx) => (
                      <TableRow key={`${e.engineerId}-${e.date}-${idx}`} hover>
                        <TableCell>
                          {formatWorkDateDisplay(e.date) || e.date}
                        </TableCell>
                        <TableCell>
                          {e.engineerName || engineerName(e.engineerId)}
                        </TableCell>
                        <TableCell style={{ textTransform: 'capitalize' }}>
                          {(e.category || '').replace(/_/g, ' ')}
                        </TableCell>
                        <TableCell>{e.multiplier}</TableCell>
                        <TableCell>{e.totalHrs}</TableCell>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {deptRows.length > 0 ? (
                <Typography className={tableClasses.scrollHint}>
                  {deptInfinite.hasMore
                    ? `Showing ${deptInfinite.visibleCount} of ${deptInfinite.totalCount} — scroll for more`
                    : `Showing all ${deptInfinite.totalCount} preview entries (latest ${previewLimit})`}
                </Typography>
              ) : null}
            </TableContainer>
          </Box>
        </>
      )}
    </Box>
  );
};

export default TimesheetExport;
